import {
  ApplicationLocale,
  cleanLocalStorage,
  ConfigObjectType,
  DateUtils,
  getIsLinkActive,
  getLocalStorage,
  useCurrentLocale,
  useLocalStorage,
} from '@wallaby-ui/core';
import {sortBy} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {useInstanceLocation, usePermissions} from 'src/js/containers';

// dynamic items
export type DynamicItemId = {
  routeId: string;
  id: string | number;
};
export type DynamicItemDefinition = DynamicItemId & {
  isLoading?: boolean;
  isPrefix?: boolean;
  paramsString: string; // NOTE: "/123"
  label: string;
  cleanLocalStorage?: string[]; // NOTE: ["/encounter/123"]
  query?: Record<string, any>; // eslint-disable-line
  isPatient?: boolean;
  redirectOnClose?: string; // NOTE: "/"
  iconClass?: string; // NOTE: font awesome icon "fas fa-bed"
  iconName?: string; // NOTE: icon in `packages/core/assets/icons/${iconName}.svg`
  iconViewbox?: string;
};
export type DynamicItem = DynamicItemDefinition & {addDate: string};
// routes
export type StaticRouteDefinition = {
  path: string; // NOTE: "/url"
  paramsString?: string; // NOTE: /:param
  component: string; // NOTE: component defined in packages/layout/src/js/lib/layout/layout_components_definitions.ts
  label?: string;
  group?: string;
  iconClass?: string; // NOTE: font awesome icon
  iconName?: string; // NOTE: icon in packages/core/assets/icons/*.svg
  iconViewbox?: string;
  hidden?: boolean;
  roles?: string[]; // NOTE: defined in usePermissions().allRoleStrings, format: ["ROLE1 & ROLE2", "ROLE3"] => ("ROLE1" and "ROLE2") or "ROLE3"
  instanceLocations?: string[];
};
export type DynamicRouteDefinition = StaticRouteDefinition & {
  id?: string;
};
export type Route = (StaticRouteDefinition | DynamicRouteDefinition) & {
  dynamicItem?: DynamicItem;
};
export type RouteGroup = {
  id?: string;
  ids?: string[];
  label: string;
};
export type NavigationConfig = {
  appName: string; // NOTE: "HAIDI"
  index: string; // NOTE: "/hospitalizedPatients"
  loadingComponent: string; // NOTE: "LoadingSpinnerPage"
  notFoundComponent: string; // NOTE: "NotFoundComponentBox"
  staticRoutes: StaticRouteDefinition[];
  staticGroups: RouteGroup[];
  dynamicRoutes: DynamicRouteDefinition[];
  dynamicGroups: RouteGroup[];
};
export type NavigationContext = {
  isLoading: boolean;
  navConfig: NavigationConfig;
  staticRoutesToShow: [RouteGroup, Route[]][];
  dynamicRoutesToShow: [RouteGroup, Route[]][];
  indexRoute: Route | undefined;
  isNavigationOpen: boolean;
  setIsNavigationOpen: (newValue: boolean) => void;
  isUserMenuOpen: boolean;
  setUserMenuOpen: (newValue: boolean) => void;
  removeDynamicItem: (itemToDelete: DynamicItemId) => void;
  removeDynamicItemGroup: (group: RouteGroup) => void;
};
function routeIsInGroup(route: Route, group: RouteGroup): boolean {
  const groupIds = group.ids ?? [String(group.id)];
  return groupIds.includes(String(route.group ?? (route as DynamicRouteDefinition).id));
}
function haveRouteRoles(route: Route, allRoleStrings: string[]) {
  const orRoles = route.roles ?? [];
  const orAndRoles = orRoles.map((v) => v.split('&'));
  return orAndRoles.length === 0 || orAndRoles.some((andRoles) => andRoles.every((role) => allRoleStrings.includes(role.trim())));
}
function instanceLocationMatches(route: Route, currentInstanceLocation: string | undefined) {
  if (currentInstanceLocation == null || route.instanceLocations == null || route.instanceLocations.length === 0) {
    return true;
  }
  return (route.instanceLocations || [currentInstanceLocation]).indexOf(currentInstanceLocation?.toUpperCase()) >= 0;
}
function getDynamicItemRoute(item: DynamicItem, dynamicRoutes: Route[]): Route | undefined {
  return dynamicRoutes.find((route) => (route as DynamicRouteDefinition).id === item.routeId);
}
function dynamicItemIsInGroup(item: DynamicItem, group: RouteGroup, dynamicRoutes: Route[]): boolean {
  const route = getDynamicItemRoute(item, dynamicRoutes);
  return Boolean(route && routeIsInGroup(route, group));
}
function getRoutesToShow(
  allRoleStrings: string[],
  _routes: Route[] | undefined,
  _groups: RouteGroup[] | undefined,
  dynamicItems?: DynamicItem[],
  currentInstanceLocation?: string
) {
  let routes = (_routes ?? []).filter((v) => !v.hidden);
  if (dynamicItems != null) {
    routes = routes.flatMap((route) =>
      dynamicItems
        .filter((v) => v.routeId === (route as DynamicRouteDefinition).id)
        .map((dynamicItem) => ({
          ...route,
          group: route.group ?? (route as DynamicRouteDefinition).id,
          dynamicItem,
        }))
    );
  }
  const allowedRoutes: [RouteGroup, Route[]][] = [];
  const groups: RouteGroup[] = [
    {
      id: 'undefined',
      label: '',
    },
    ...(_groups ?? []),
  ];
  routes.forEach((route) => {
    if (!groups.find((group) => routeIsInGroup(route, group))) {
      const groupId = String(route.group);
      groups.push({id: groupId, label: groupId});
      console.warn(`Unknown route group:`, groupId);
    }
  });
  (groups ?? []).forEach((group) => {
    const routesInGroup = sortBy(
      routes.filter(
        (route) =>
          routeIsInGroup(route, group) && haveRouteRoles(route, allRoleStrings) && instanceLocationMatches(route, currentInstanceLocation)
      ),
      (v) => new Date(v.dynamicItem?.addDate ?? 0)
    );
    allowedRoutes.push([group, routesInGroup]);
  });
  return allowedRoutes;
}
function pathToRegex(path: string, paramsString?: string) {
  const full = `${path}${paramsString ?? ''}`
    .replace(/\/:([^/?]+)\?/g, '(?:/[^/]+)?')
    .replace(/\/:([^/?]+)/g, '/[^/]+')
    .replace(/\?/g, '');
  return new RegExp(`^${full}$`);
}
function findRouteForPath(pathname: string, routes: Route[]) {
  return routes.find((r) => pathToRegex(r.path, r.paramsString).test(pathname));
}
const DYNAMIC_ITEMS_STORAGE_ID = '/useRoutes';
const appNameState = {appName: ''};
export function useAppName() {
  return appNameState;
}
export function useNavigationContext(config: ConfigObjectType, navigationConfig: NavigationConfig): NavigationContext {
  // routes
  const [dynamicItems, setDynamicItems] = useLocalStorage(DYNAMIC_ITEMS_STORAGE_ID, [] as DynamicItem[], {
    listenToSamePage: true,
    listenToOtherPage: true,
  });
  const navConfig: NavigationConfig = useMemo(
    () => ({
      ...navigationConfig,
      dynamicItems,
    }),
    [navigationConfig, dynamicItems]
  );
  appNameState.appName = navConfig.appName;
  const {isLoading, allRoleStrings} = usePermissions(config, true);
  const {instanceLocation} = useInstanceLocation(config);
  const staticRoutesToShow = useMemo(
    () => getRoutesToShow(allRoleStrings, navConfig.staticRoutes, navConfig.staticGroups, undefined, instanceLocation),
    [allRoleStrings, navConfig, instanceLocation]
  );
  const dynamicRoutesToShow = useMemo(
    () =>
      getRoutesToShow(allRoleStrings, navConfig.dynamicRoutes, navConfig.dynamicGroups, dynamicItems, instanceLocation).map(
        ([group, routes]) => {
          return [group, sortBy(routes, (v) => -new Date(v.dynamicItem?.addDate ?? ''))] as [RouteGroup, Route[]];
        }
      ),
    [allRoleStrings, navConfig, dynamicItems, instanceLocation]
  );
  const indexRoute = useMemo(() => {
    const path = navConfig.index ?? '/';
    const flatRoutesToShow = staticRoutesToShow.flatMap(([_group, groupRoutes]) => groupRoutes);
    return flatRoutesToShow.find((route) => route.path === path) ?? flatRoutesToShow[0];
  }, [navConfig, staticRoutesToShow]);
  // hover and buttons
  const [isNavigationOpen, _3, setIsNavigationOpen] = useLocalStorage('/NavigationBox.isOpen', true);
  const [isUserMenuOpen, setUserMenuOpen] = useState(false);
  // remove dynamic items
  const navigate = useNavigate();
  const onRemoveDynamicItem = useCallback(
    (item: DynamicItem) => {
      if (item.cleanLocalStorage) {
        item.cleanLocalStorage.forEach((prefix) => {
          cleanLocalStorage(prefix);
        });
      }
      const route = getDynamicItemRoute(item, navConfig.dynamicRoutes);
      if (!route) return;
      const pathWithParams = `${route.path}${item.paramsString ?? ''}`;
      const isLinkActive = getIsLinkActive(pathWithParams, item.isPrefix, window.location.pathname, navConfig.index);
      if (isLinkActive) {
        const redirectTo = item.redirectOnClose ?? '/';
        navigate(redirectTo);
      }
    },
    [navConfig, navigate]
  );
  const removeDynamicItem = useCallback(
    (itemToDelete: DynamicItemId) => {
      const newDynamicItems = [...dynamicItems];
      const i = newDynamicItems.findIndex((item) => `${item.routeId}-${item.id}` === `${itemToDelete.routeId}-${itemToDelete.id}`);
      if (i !== -1) {
        onRemoveDynamicItem(newDynamicItems[i]);
        newDynamicItems.splice(i, 1);
        setDynamicItems(newDynamicItems);
      }
    },
    [dynamicItems, setDynamicItems, onRemoveDynamicItem]
  );
  const removeDynamicItemGroup = useCallback(
    (group: RouteGroup) => {
      const itemsToDelete = dynamicItems.filter((item) => dynamicItemIsInGroup(item, group, navConfig.dynamicRoutes));
      const newDynamicItems = dynamicItems.filter((item) => !dynamicItemIsInGroup(item, group, navConfig.dynamicRoutes));
      itemsToDelete.forEach((item) => {
        onRemoveDynamicItem(item);
      });
      setDynamicItems(newDynamicItems);
    },
    [navConfig, dynamicItems, setDynamicItems, onRemoveDynamicItem]
  );
  useEffect(() => {
    if (isLoading) return;

    const {pathname} = window.location;

    const allRoutes: Route[] = [...(navConfig.staticRoutes ?? []), ...(navConfig.dynamicRoutes ?? [])];

    const matched = findRouteForPath(pathname, allRoutes);
    if (!matched) return;

    const allowed = haveRouteRoles(matched, allRoleStrings);
    if (!allowed) {
      const fallback = navConfig.index ?? '/';
      navigate(fallback, {replace: true, state: {deniedFrom: pathname}});
    }
  }, [isLoading, allRoleStrings, navConfig, navigate]);
  return {
    isLoading,
    navConfig,
    staticRoutesToShow,
    dynamicRoutesToShow,
    indexRoute,
    isNavigationOpen,
    setIsNavigationOpen,
    isUserMenuOpen,
    setUserMenuOpen,
    removeDynamicItem,
    removeDynamicItemGroup,
  };
}
type UseAddDynamicItem = {
  addDynamicItem: (newItem: DynamicItemDefinition) => void;
};
export function useAddDynamicItem(): UseAddDynamicItem {
  const [_, setDynamicItems] = useLocalStorage(DYNAMIC_ITEMS_STORAGE_ID, [] as DynamicItem[]);
  const getDynamicItems = useCallback(() => {
    return getLocalStorage(DYNAMIC_ITEMS_STORAGE_ID, [] as DynamicItem[]);
  }, []);
  const addDynamicItem = useCallback(
    (newItemDef: DynamicItemDefinition) => {
      const newDynamicItems = [...getDynamicItems()];
      const i = newDynamicItems.findIndex((item) => `${item.routeId}-${item.id}` === `${newItemDef.routeId}-${newItemDef.id}`);
      const newItem = {
        ...newItemDef,
        addDate: i === -1 ? DateUtils.toIsoDateTimeString(new Date()) : newDynamicItems[i].addDate,
      };
      if (i === -1) {
        if (!newItem.isLoading) {
          // add only if not loading
          newDynamicItems.push(newItem);
          setDynamicItems(newDynamicItems);
        }
      } else if (!newItem.isLoading && JSON.stringify(newItem) !== JSON.stringify(newDynamicItems[i])) {
        // NOTE: _.isEqual() is broken
        // update only if not loading
        newDynamicItems[i] = newItem;
        setDynamicItems(newDynamicItems);
      }
    },
    [getDynamicItems, setDynamicItems]
  );
  return {
    addDynamicItem,
  };
}
// values to copy
export type ValuesToCopy = Record<string, any> | null;
export function useValuesToCopy() {
  const [valuesToCopy, _setValuesToCopy] = useState(null as ValuesToCopy);
  const setValuesToCopy = useCallback(
    (newValuesToCopy: ValuesToCopy) => {
      if (JSON.stringify(newValuesToCopy) === JSON.stringify(valuesToCopy)) return;
      setTimeout(() => {
        _setValuesToCopy(newValuesToCopy);
      }, 0);
    },
    [valuesToCopy]
  );
  const currentPath = window.location.pathname;
  useEffect(() => {
    if (valuesToCopy !== null) setValuesToCopy(null);
  }, [currentPath]); // eslint-disable-line
  return {valuesToCopy, setValuesToCopy};
}
